import PocketBase, {
  BaseAuthStore,
  ClientResponseError,
  LocalAuthStore,
  type AuthRecord,
} from "pocketbase"

import type { TenantAccess } from "@/lib/api/types"
import { env } from "@/lib/env"
import { getTenantApiUrl } from "@/lib/tenant"

/**
 * The two PocketBase servers this app talks to.
 *
 * MainApp knows who you are and which businesses you belong to. Each business's
 * data lives on one of the Application servers, and which one is only known
 * after signing in — so the tenant client is pointed at its server at runtime.
 *
 * They must not share an auth store. The SDK defaults every client to the same
 * `pb_auth` localStorage key, so two clients on one page would quietly
 * overwrite each other's token and the app would flip between identities.
 */

export const mainPb = new PocketBase(env.mainAppUrl, new LocalAuthStore("manito.pb.main"))

/** Which business a tab's tenant session is for, as the session's row says it. */
interface BoundBusiness {
  id: string
  support: boolean
}

const TENANT_SESSION_PREFIX = "manito.pb.tenant."

function keyFor(business: BoundBusiness): string {
  return `${TENANT_SESSION_PREFIX}${business.id}${business.support ? ".support" : ""}`
}

/**
 * The tenant session, kept per business rather than once per browser
 * (MANIT-1163).
 *
 * A browser holds one localStorage for every tab, and the business being
 * worked in is chosen per tab. With a single key, opening a second business
 * in a second tab wrote its session over the first tab's: that tab went on
 * showing the first business by name while every request carried the
 * second's session, so on a shared server its bookings and customer edits
 * were filed in the other shop. Here each tab binds the store to its own
 * business, and the session is read from and written to that business's
 * key alone. Two tabs on the same business still share one session, as they
 * should, and a renewal in one is seen by the other.
 *
 * A support visit is a different seat from the same person's own membership
 * of that shop, so it has a key of its own as well.
 */
class TenantAuthStore extends BaseAuthStore {
  private bound: BoundBusiness | null = null

  constructor() {
    super()
    if (typeof window === "undefined") return

    // The one key every tab shared before this. Whatever it holds may be any
    // business's, so it is never read: the next request opens a fresh one.
    window.localStorage.removeItem("manito.pb.tenant")

    window.addEventListener("storage", (event) => {
      const key = this.key()
      if (key && event.key === key) super.save(this.token, this.record)
    })
  }

  /** The business this tab is working in; null when it is in none. */
  get business(): BoundBusiness | null {
    return this.bound
  }

  bind(tenant: Pick<TenantAccess, "id" | "support"> | null): void {
    const next = tenant ? { id: tenant.id, support: tenant.support === true } : null
    if (next?.id === this.bound?.id && next?.support === this.bound?.support) return
    this.bound = next
    // Anything listening hears the session it now reads, not the last one.
    super.save(this.token, this.record)
  }

  get token(): string {
    return this.read()?.token ?? ""
  }

  get record(): AuthRecord {
    return this.read()?.record ?? null
  }

  get model(): AuthRecord {
    return this.record
  }

  save(token: string, record?: AuthRecord): void {
    const key = this.key()
    // A session with no business to file it under belongs to nobody; keeping
    // it would let the next business picked in this tab inherit it.
    if (key && typeof window !== "undefined") {
      window.localStorage.setItem(key, JSON.stringify({ token, record: record ?? null }))
    }
    super.save(key ? token : "", key ? record : null)
  }

  /**
   * Files a session under the business it was opened for. Only when that is
   * the business this tab is in does it become the session in use.
   */
  saveFor(tenant: Pick<TenantAccess, "id" | "support">, token: string, record: AuthRecord): void {
    const key = keyFor({ id: tenant.id, support: tenant.support === true })
    if (key !== this.key()) {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(key, JSON.stringify({ token, record }))
      }
      return
    }
    this.save(token, record)
  }

  clear(): void {
    const key = this.key()
    if (key && typeof window !== "undefined") window.localStorage.removeItem(key)
    super.clear()
  }

  /** Forgets the session of every business, for signing out. */
  clearAll(): void {
    if (typeof window !== "undefined") {
      for (let i = window.localStorage.length - 1; i >= 0; i--) {
        const key = window.localStorage.key(i)
        if (key?.startsWith(TENANT_SESSION_PREFIX)) window.localStorage.removeItem(key)
      }
    }
    super.clear()
  }

  private key(): string | null {
    return this.bound ? keyFor(this.bound) : null
  }

  private read(): { token?: string; record?: AuthRecord } | null {
    const key = this.key()
    if (!key) return null
    if (typeof window === "undefined") return { token: this.baseToken, record: this.baseModel }
    try {
      return JSON.parse(window.localStorage.getItem(key) ?? "null")
    } catch {
      return null
    }
  }
}

export const tenantAuthStore = new TenantAuthStore()

export const tenantPb = new PocketBase(
  // The last business worked in, so a reload does not have to wait for the
  // selection to be rehydrated before it can make a request.
  getTenantApiUrl() ?? env.mainAppUrl,
  tenantAuthStore,
)

/**
 * Points this tab's tenant session at a business, or at none. Every place
 * that changes the business a tab works in calls it, before any request for
 * the new business is made.
 */
export function bindTenantSession(tenant: Pick<TenantAccess, "id" | "support"> | null): void {
  tenantAuthStore.bind(tenant)
}

/**
 * The SDK cancels a pending request when an identical one is made, which is
 * exactly what react-query does when two mounted screens want the same list.
 * Left on, the second caller wins and the first sees an abort error.
 */
mainPb.autoCancellation(false)
tenantPb.autoCancellation(false)

/**
 * Every write is attributed to the dashboard in the audit log. Without it the
 * server can only record that something came over HTTP, and a booking the
 * customer made themselves would read the same as one the front desk typed in.
 */
tenantPb.beforeSend = (url, options) => {
  assertSessionMatchesBusiness(url)
  options.headers = { ...options.headers, "X-Client": "web" }
  return { url, options }
}

/**
 * The last check before a request leaves, whatever the store holds: a
 * session for another business, or for the other kind of seat in this one,
 * is never sent under this tab's business (MANIT-1163). The server stamps
 * the shop from the session, so a mismatch would read and write the other
 * shop's data under this one's name.
 *
 * Answered as a dead session, which the dashboard already repairs by
 * walking the sign-in chain again for the right business.
 */
function assertSessionMatchesBusiness(url: string): void {
  const record = tenantAuthStore.record
  if (!record) return

  const business = tenantAuthStore.business
  const matches =
    business !== null &&
    record.tenant_id === business.id &&
    Boolean(record.support) === business.support
  if (matches) return

  tenantAuthStore.clear()
  throw new ClientResponseError({
    url,
    status: 401,
    response: { message: "The session belongs to another business." },
  })
}

/**
 * Photographs of people are protected on the shard: a customer's photo and a
 * team member's face open only with a file token from somebody allowed to see
 * the record (Application migration 1790531307_protected_photos). Everything
 * else — service and class pictures, the logo — is public, because the
 * booking page shows it.
 *
 * A token lasts an hour. One is held per tenant session and renewed when it
 * has ten minutes left. Renewal happens after any tenant response, before the
 * caller turns records into addresses, so every address built from fresh data
 * carries a token that works — without every fetcher having to ask for one.
 */
const FILE_TOKEN_RENEW_MS = 10 * 60 * 1000

let fileToken: { token: string; until: number; session: string } | null = null
let fileTokenPending: Promise<void> | null = null

/** When a JWT stops working, from its own `exp`. */
function tokenExpiry(token: string) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))) as {
      exp?: unknown
    }
    return typeof payload.exp === "number" ? payload.exp * 1000 : 0
  } catch {
    return 0
  }
}

/** Makes sure a usable file token is held for the current tenant session. */
export async function ensureFileToken(): Promise<void> {
  const session = tenantPb.authStore.token
  if (!session) return
  if (
    fileToken &&
    fileToken.session === session &&
    fileToken.until - Date.now() > FILE_TOKEN_RENEW_MS
  ) {
    return
  }

  fileTokenPending ??= tenantPb.files
    .getToken()
    .then((token) => {
      fileToken = { token, until: tokenExpiry(token), session }
    })
    .catch((error: unknown) => {
      // A face that cannot load shows initials; the data beside it still
      // arrives, which is the right way round.
      console.warn("Could not get a file token; photos will show initials.", error)
    })
    .finally(() => {
      fileTokenPending = null
    })

  return fileTokenPending
}

/** The file token held for the current tenant session, or "" when none is. */
export function heldFileToken(): string {
  const session = tenantPb.authStore.token
  if (!fileToken || fileToken.session !== session || fileToken.until <= Date.now()) {
    return ""
  }
  return fileToken.token
}

tenantPb.afterSend = async (response, data) => {
  if (response.ok && !response.url.includes("/api/files/token")) {
    await ensureFileToken()
  }
  return data
}

/** Points the tenant client at a business's server. */
export function useTenantServer(serverUrl: string): void {
  if (tenantPb.baseURL !== serverUrl) tenantPb.baseURL = serverUrl
}
